import type { AnalysisDaySchema } from '@libs/data-access/entities'
import { AlertCircleIcon, Flex, IconWrap, Tooltip } from '@libs/ui/ds'
import { usePlanningTranslation } from '@libs/utils/translations'
import type { ReduxState } from '@spa/redux/ReduxState'
import { useMemoSelector } from '@spa/redux/hooks'
import { getParams } from '@spa/scenes/Plannings/redux/selectors'
import { cva } from 'class-variance-authority'
import moment from 'moment'
import React, { useMemo } from 'react'
import type { FC } from 'react'
import { getAccountCurrencyIsoCode } from 'snap-redux/modules/accounts'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'
import { getLocationById } from 'snap-redux/modules/locations/selectors'

import InputRevenue from './InputRevenue/InputRevenue'
import { getStatistics } from './utils/getStatistics'

const dateNow = moment().format('YYYY-MM-DD')

export const dailyStatisticsCVA = {
  root: cva(
    [
      'flex-1 flex-nowrap items-center justify-between border-r-1 border-r-neutral-200 pb-2 pt-3 text-center'
    ],
    {
      variants: {
        opaque: {
          true: 'opacity-50'
        }
      }
    }
  ),
  workedHours: cva(['flex-nowrap items-center justify-center pb-2.5']),
  productivity: cva(['flex-nowrap items-center justify-center pb-4'], {
    variants: {
      isGreen: {
        true: 'text-semantic-green-500'
      },
      isOrange: {
        true: 'text-semantic-orange-400'
      }
    }
  }),
  payroll: cva(['flex-nowrap items-center justify-center gap-x-1 pb-4']),
  payrollRatio: cva(
    ['flex-nowrap items-center justify-center pb-4 text-neutral-400'],
    {
      variants: {
        isGreen: {
          true: 'text-semantic-green-500'
        },
        isOrange: {
          true: 'text-semantic-orange-400'
        }
      }
    }
  )
}

type Props = {
  real: boolean
  data: AnalysisDaySchema
  canSeeWagesRatio: boolean
}
const DailyStatisticsColumn: FC<Props> = ({
  real = false,
  data,
  canSeeWagesRatio = false
}) => {
  const { t, tDates, tCommon } = usePlanningTranslation()

  const {
    payroll_in_euro,
    payroll_ratio,
    productivity_in_euro_per_hour,
    worked_minutes,
    date,
    alert
  } = getStatistics(real, data)

  const account = useMemoSelector(getCurrentAccount)
  const account_currency = useMemo(
    () => getAccountCurrencyIsoCode(account),
    [account]
  )
  const { locationId, teamId } = useMemoSelector(getParams)
  const location = useMemoSelector((state: ReduxState) =>
    getLocationById(state, Number(locationId))
  )

  return (
    <Flex
      className={dailyStatisticsCVA.root({ opaque: date > dateNow && real })}
    >
      <InputRevenue
        real={real}
        data={data}
        account_currency={account_currency}
        locationId={locationId}
        teamId={teamId}
      />
      <Flex row className={dailyStatisticsCVA.workedHours()}>
        {tDates('duration.minuteDecimalToHours', {
          duration: worked_minutes
        })}
      </Flex>
      <Flex
        row
        className={dailyStatisticsCVA.productivity({
          isGreen:
            productivity_in_euro_per_hour >=
            (location?.productivity_objective || 50),
          isOrange:
            productivity_in_euro_per_hour <
            (location?.productivity_objective || 50)
        })}
      >
        {`${productivity_in_euro_per_hour}${account_currency}/h`}
      </Flex>
      {canSeeWagesRatio && (
        <>
          <Flex row className={dailyStatisticsCVA.payroll()}>
            {t('planning.components.analysis.wage', {
              value: payroll_in_euro,
              currency: account_currency
            })}
            {Boolean(alert.missing_salaries.length) && (
              <Tooltip
                label={t('planning.components.statisticsColumn.tooltip', {
                  users: alert.missing_salaries
                    .filter(v => Boolean(v))
                    .map(user => tCommon('models.fullname', { value: user }))
                    .join(', ')
                })}
              >
                <IconWrap icon={<AlertCircleIcon />} />
              </Tooltip>
            )}
          </Flex>
          <Flex
            row
            className={dailyStatisticsCVA.payrollRatio({
              isGreen: payroll_ratio > 0,
              isOrange: payroll_ratio > (location?.wage_ratio_objective || 30)
            })}
          >
            {t('planning.components.analysis.wageRatio', {
              value: payroll_ratio
            })}
          </Flex>
        </>
      )}
    </Flex>
  )
}

export default DailyStatisticsColumn
